import { getCurrentScope } from '@sentry/browser';
import * as Sentry from '@sentry/browser';
import { fullStoryIntegration } from '@sentry/fullstory';
import { FullStory } from '@fullstory/browser';
import type { SentryInitOptions, SentryLogOptions, TTags } from './types';

class SentryService {
  isOn: boolean;
  DSN?: string | null;
  release?: string;
  environment?: string;
  tags: TTags;
  extra: SentryLogOptions['extra'];

  constructor({
    isOn = false,
    DSN = null,
    release = window.__SENTRY_RELEASE__,
    environment = window.__SENTRY_ENVIRONMENT__,
    tags = {} as TTags,
    extra = {},
  }: Partial<SentryInitOptions>) {
    this.isOn = isOn;
    this.DSN = DSN;
    this.tags = tags;
    this.extra = extra;
    this.environment = environment;
    if (release) {
      this.release = release;
    }
    this.init();
  }

  init(): void {
    if (!this.isOn || !this.DSN) {
      return;
    }

    Sentry.init({
      dsn: this.DSN,
      release: this.release,
      environment: this.environment,
      attachStacktrace: true,
      integrations: [fullStoryIntegration('gloat', { client: FullStory })],
      // https://github.com/getsentry/sentry-javascript/issues/3440
      // we want to ignore these weird sentry errors that coming from some third party script or browser extension
      // for more details please see the github issue
      ignoreErrors: [
        /Non-Error promise rejection captured/,
        /Object Not Found Matching Id:?[0-9]*/,
        /__gCrWeb/,
        'retry',
        'Failed to fetch',
      ],
      denyUrls: [/login\.microsoftonline\.com/],
    });
  }

  log: (message: Error | string, options?: SentryLogOptions) => void = (message, options = {}) => {
    const { extra = {}, tags = {}, level = 'debug' } = options;

    if (!this.isOn) {
      return;
    }

    Sentry.withScope(scope => {
      scope.setLevel(level);

      const combinedTags = { ...this.tags, ...tags };
      Object.entries(combinedTags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });

      const combinedExtra = { ...this.extra, ...extra };
      Object.entries(combinedExtra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });

      if (typeof message === 'string') {
        Sentry.captureMessage(message);
      } else {
        Sentry.captureException(message);
      }
    });
    const scope = getCurrentScope();
    scope.clear();
  };
}

export default SentryService;
